import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import CategoryManager from "../components/CategoryManager";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Loader2,
  Plus,
  Trash2,
  Package,
  Wrench,
  AlertTriangle,
  DollarSign,
  Edit,
  Eye,
  ChevronLeft,
  CheckCircle,
  XCircle,
  ClipboardCheck,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EquipmentManagement = () => {
  const { isOfficeManager } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  // iter138: honour ?tab= query param so sidebar links don't all land here.
  const initialTab = searchParams.get("tab") || "equipment";
  const [activeTab, setActiveTab] = useState(initialTab);
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && t !== activeTab) setActiveTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const handleTabChange = (t) => {
    setActiveTab(t);
    const next = new URLSearchParams(searchParams);
    if (t === "equipment") next.delete("tab"); else next.set("tab", t);
    setSearchParams(next, { replace: true });
  };
  const [equipment, setEquipment] = useState([]);
  const [stats, setStats] = useState(null);
  const [inspectionsDue, setInspectionsDue] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterCategory, setFilterCategory] = useState("");
  
  // Selected equipment for detail view
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  
  // Dialog states
  const [equipmentDialogOpen, setEquipmentDialogOpen] = useState(false);
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  // Form states
  const [equipmentForm, setEquipmentForm] = useState({
    name: "", category: "other", serial_number: "", purchase_date: "",
    purchase_price: "", warranty_expiry: "", condition: "good",
    location: "", assigned_to: "", last_inspection_date: "",
    next_inspection_date: "", certification_required: false,
    certification_expiry: "", notes: ""
  });
  
  const [maintenanceForm, setMaintenanceForm] = useState({
    maintenance_type: "inspection", date: new Date().toISOString().split("T")[0],
    cost: "", performed_by: "", description: "", next_due_date: "",
    passed_inspection: null
  });

  const fetchEquipment = useCallback(async () => {
    try {
      const url = filterCategory 
        ? `${API}/equipment?category=${filterCategory}`
        : `${API}/equipment`;
      const response = await axios.get(url);
      setEquipment(response.data);
    } catch (error) {
      console.error("Failed to fetch equipment", error);
    }
  }, [filterCategory]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/equipment/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats", error);
    }
  }, []);

  const fetchInspectionsDue = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/equipment/inspections-due?days_ahead=30`);
      setInspectionsDue(response.data);
    } catch (error) {
      console.error("Failed to fetch inspections due", error);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error("Failed to fetch employees", error);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/equipment/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch categories", error);
    }
  }, []);

  const fetchEquipmentDetail = async (equipmentId) => {
    try {
      const response = await axios.get(`${API}/equipment/${equipmentId}`);
      setSelectedEquipment(response.data);
    } catch (error) {
      toast.error("Failed to load equipment details");
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchEquipment(), fetchStats(), fetchInspectionsDue(), fetchEmployees(), fetchCategories()]);
      setLoading(false);
      
      // Check for quick action intent
      const quickAction = sessionStorage.getItem('quickAction');
      if (quickAction === 'add-equipment') {
        sessionStorage.removeItem('quickAction');
        resetEquipmentForm();
        setEquipmentDialogOpen(true);
      }
    };
    loadData();
  }, [fetchEquipment, fetchStats, fetchInspectionsDue, fetchEmployees, fetchCategories]);

  useEffect(() => {
    fetchEquipment();
  }, [filterCategory, fetchEquipment]);

  const resetEquipmentForm = () => {
    setEquipmentForm({
      name: "", category: "other", serial_number: "", purchase_date: "",
      purchase_price: "", warranty_expiry: "", condition: "good",
      location: "", assigned_to: "", last_inspection_date: "",
      next_inspection_date: "", certification_required: false,
      certification_expiry: "", notes: ""
    });
    setEditMode(false);
  };

  const handleCreateEquipment = async () => {
    if (!equipmentForm.name) {
      toast.error("Equipment name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...equipmentForm,
        purchase_price: equipmentForm.purchase_price ? parseFloat(equipmentForm.purchase_price) : null,
      };
      
      if (editMode && selectedEquipment) {
        await axios.put(`${API}/equipment/${selectedEquipment.id}`, payload);
        toast.success("Equipment updated");
      } else {
        await axios.post(`${API}/equipment`, payload);
        toast.success("Equipment added");
      }
      
      setEquipmentDialogOpen(false);
      resetEquipmentForm();
      fetchEquipment();
      fetchStats();
    } catch (error) {
      toast.error("Failed to save equipment");
    } finally {
      setSaving(false);
    }
  };

  const handleEditEquipment = (item) => {
    setEquipmentForm({
      name: item.name || "",
      category: item.category || "other",
      serial_number: item.serial_number || "",
      purchase_date: item.purchase_date || "",
      purchase_price: item.purchase_price?.toString() || "",
      warranty_expiry: item.warranty_expiry || "",
      condition: item.condition || "good",
      location: item.location || "",
      assigned_to: item.assigned_to || "",
      last_inspection_date: item.last_inspection_date || "",
      next_inspection_date: item.next_inspection_date || "",
      certification_required: item.certification_required || false,
      certification_expiry: item.certification_expiry || "",
      notes: item.notes || ""
    });
    setSelectedEquipment(item);
    setEditMode(true);
    setEquipmentDialogOpen(true);
  };

  const handleDeleteEquipment = async (equipmentId) => {
    if (!window.confirm("Are you sure you want to retire this equipment?")) return;
    try {
      await axios.delete(`${API}/equipment/${equipmentId}`);
      toast.success("Equipment retired");
      fetchEquipment();
      fetchStats();
      setSelectedEquipment(null);
    } catch (error) {
      toast.error("Failed to delete equipment");
    }
  };

  const handleAddMaintenance = async () => {
    if (!maintenanceForm.date || !maintenanceForm.maintenance_type) {
      toast.error("Date and type are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...maintenanceForm,
        equipment_id: selectedEquipment.id,
        cost: maintenanceForm.cost ? parseFloat(maintenanceForm.cost) : 0,
      };
      await axios.post(`${API}/equipment/${selectedEquipment.id}/maintenance`, payload);
      toast.success("Maintenance record added");
      setMaintenanceDialogOpen(false);
      setMaintenanceForm({
        maintenance_type: "inspection", date: new Date().toISOString().split("T")[0],
        cost: "", performed_by: "", description: "", next_due_date: "",
        passed_inspection: null
      });
      fetchEquipmentDetail(selectedEquipment.id);
      fetchInspectionsDue();
      fetchStats();
    } catch (error) {
      toast.error("Failed to add maintenance");
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => `$${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const getConditionColor = (condition) => {
    switch (condition) {
      case "excellent": return "bg-emerald-500";
      case "good": return "bg-green-500";
      case "fair": return "bg-yellow-500";
      case "poor": return "bg-orange-500";
      case "retired": return "bg-gray-500";
      default: return "bg-gray-500";
    }
  };

  const getCategoryLabel = (value) => {
    const cat = categories.find(c => c.value === value);
    return cat ? cat.label : value;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="equipment-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Equipment Detail View
  if (selectedEquipment && !equipmentDialogOpen) {
    return (
      <div className="space-y-6 animate-in" data-testid="equipment-detail">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setSelectedEquipment(null)}>
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Equipment
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
              <Package className="h-8 w-8 text-primary" />
              {selectedEquipment.name}
            </h1>
            <p className="text-muted-foreground mt-1">
              {getCategoryLabel(selectedEquipment.category)} • {selectedEquipment.serial_number || "No serial number"}
            </p>
          </div>
          {isOfficeManager && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => handleEditEquipment(selectedEquipment)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button variant="outline" onClick={() => setMaintenanceDialogOpen(true)}>
                <Wrench className="h-4 w-4 mr-2" />
                Add Maintenance
              </Button>
              <Button variant="destructive" onClick={() => handleDeleteEquipment(selectedEquipment.id)}>
                <Trash2 className="h-4 w-4 mr-2" />
                Retire
              </Button>
            </div>
          )}
        </div>

        {/* Equipment Info Cards */}
        <div className="grid md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Condition</p>
              <Badge className={getConditionColor(selectedEquipment.condition)}>
                {selectedEquipment.condition}
              </Badge>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Purchase Value</p>
              <p className="text-2xl font-bold">
                {selectedEquipment.purchase_price ? formatCurrency(selectedEquipment.purchase_price) : "N/A"}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Maintenance Cost</p>
              <p className="text-2xl font-bold text-orange-600">
                {formatCurrency(selectedEquipment.total_maintenance_cost)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Total Cost</p>
              <p className="text-2xl font-bold">
                {formatCurrency(selectedEquipment.total_cost_of_ownership)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Equipment Details */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Equipment Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-muted-foreground">Serial Number:</span>
                <span>{selectedEquipment.serial_number || "N/A"}</span>
                <span className="text-muted-foreground">Category:</span>
                <span>{getCategoryLabel(selectedEquipment.category)}</span>
                <span className="text-muted-foreground">Purchase Date:</span>
                <span>{selectedEquipment.purchase_date || "N/A"}</span>
                <span className="text-muted-foreground">Warranty Expiry:</span>
                <span>{selectedEquipment.warranty_expiry || "N/A"}</span>
                <span className="text-muted-foreground">Location:</span>
                <span>{selectedEquipment.location || "N/A"}</span>
                <span className="text-muted-foreground">Assigned To:</span>
                <span>{selectedEquipment.assigned_employee?.name || "Unassigned"}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Inspections & Certifications</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-muted-foreground">Last Inspection:</span>
                <span>{selectedEquipment.last_inspection_date || "Never"}</span>
                <span className="text-muted-foreground">Next Inspection:</span>
                <span className={selectedEquipment.inspection_overdue ? "text-red-500 font-medium" : ""}>
                  {selectedEquipment.next_inspection_date || "Not scheduled"}
                  {selectedEquipment.inspection_overdue && " (OVERDUE)"}
                </span>
                <span className="text-muted-foreground">Certification Required:</span>
                <span>{selectedEquipment.certification_required ? "Yes" : "No"}</span>
                {selectedEquipment.certification_required && (
                  <>
                    <span className="text-muted-foreground">Certification Expiry:</span>
                    <span className={selectedEquipment.certification_expired ? "text-red-500 font-medium" : ""}>
                      {selectedEquipment.certification_expiry || "N/A"}
                      {selectedEquipment.certification_expired && " (EXPIRED)"}
                    </span>
                  </>
                )}
              </div>
              {selectedEquipment.notes && (
                <div className="pt-2 border-t">
                  <p className="text-sm text-muted-foreground mb-1">Notes:</p>
                  <p className="text-sm">{selectedEquipment.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Maintenance History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Maintenance History</CardTitle>
          </CardHeader>
          <CardContent>
            {selectedEquipment.maintenance_history?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Performed By</TableHead>
                    <TableHead>Result</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead>Next Due</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedEquipment.maintenance_history.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell>{m.date}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{m.maintenance_type}</Badge>
                      </TableCell>
                      <TableCell>{m.performed_by || "-"}</TableCell>
                      <TableCell>
                        {m.passed_inspection === true && <CheckCircle className="h-4 w-4 text-green-500" />}
                        {m.passed_inspection === false && <XCircle className="h-4 w-4 text-red-500" />}
                        {m.passed_inspection === null && "-"}
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(m.cost)}</TableCell>
                      <TableCell>{m.next_due_date || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-center text-muted-foreground py-8">No maintenance records</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="equipment-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
            <Package className="h-8 w-8 text-primary" />
            Equipment Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Track equipment inventory, inspections, and maintenance
          </p>
        </div>
        {isOfficeManager && (
          <Button onClick={() => { resetEquipmentForm(); setEquipmentDialogOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Add Equipment
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Package className="h-4 w-4" />
                <span className="text-sm">Total Equipment</span>
              </div>
              <p className="text-2xl font-bold">{stats.total_equipment}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <DollarSign className="h-4 w-4" />
                <span className="text-sm">Total Value</span>
              </div>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(stats.total_purchase_value)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Wrench className="h-4 w-4" />
                <span className="text-sm">YTD Maintenance</span>
              </div>
              <p className="text-2xl font-bold text-orange-600">{formatCurrency(stats.ytd_maintenance_cost)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm">Needs Attention</span>
              </div>
              <p className="text-2xl font-bold text-red-600">{stats.items_needing_attention}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="equipment" data-testid="tab-equipment">Equipment</TabsTrigger>
          <TabsTrigger value="inspections" data-testid="tab-inspections">Inspections Due</TabsTrigger>
        </TabsList>

        <TabsContent value="equipment" className="space-y-4">
          {/* Category Filter */}
          <div className="flex items-center gap-4">
            <Label>Category:</Label>
            <CategoryManager
              categoryType="equipment"
              label="All Categories"
              onCategoriesChange={fetchCategories}
              showAsDropdown={true}
              selectedValue={filterCategory}
              onSelectionChange={(v) => setFilterCategory(v === "all" ? "" : v)}
              allowAll={true}
            />
          </div>

          {equipment.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {equipment.map((item) => (
                <Card key={item.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => fetchEquipmentDetail(item.id)}>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{item.name}</CardTitle>
                        <CardDescription>
                          {getCategoryLabel(item.category)}
                        </CardDescription>
                      </div>
                      <Badge className={getConditionColor(item.condition)}>
                        {item.condition}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Serial #</span>
                        <span>{item.serial_number || "N/A"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Location</span>
                        <span>{item.location || "N/A"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Assigned To</span>
                        <span>{item.assigned_to_name || "Unassigned"}</span>
                      </div>
                      {item.inspection_overdue && (
                        <div className="flex items-center gap-2 text-red-600">
                          <AlertTriangle className="h-4 w-4" />
                          <span>Inspection Overdue</span>
                        </div>
                      )}
                      {item.certification_expired && (
                        <div className="flex items-center gap-2 text-red-600">
                          <AlertTriangle className="h-4 w-4" />
                          <span>Certification Expired</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No equipment found</p>
                {isOfficeManager && (
                  <Button className="mt-4" onClick={() => { resetEquipmentForm(); setEquipmentDialogOpen(true); }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Equipment
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="inspections" className="space-y-4">
          {inspectionsDue.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Equipment</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inspectionsDue.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{item.equipment.name}</TableCell>
                    <TableCell>{getCategoryLabel(item.equipment.category)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{item.type}</Badge>
                    </TableCell>
                    <TableCell>{item.due_date}</TableCell>
                    <TableCell>
                      <Badge variant={item.overdue ? "destructive" : "outline"}>
                        {item.overdue ? "OVERDUE" : "Upcoming"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => fetchEquipmentDetail(item.equipment.id)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <ClipboardCheck className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No inspections due in the next 30 days</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Equipment Dialog */}
      <Dialog open={equipmentDialogOpen} onOpenChange={setEquipmentDialogOpen}>
        <DialogContent className="max-w-2xl p-0">
          <DialogHeader className="flex-shrink-0 p-6 pb-2">
            <DialogTitle>{editMode ? "Edit Equipment" : "Add Equipment"}</DialogTitle>
            <DialogDescription>Enter equipment details</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 min-h-0">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={equipmentForm.name} onChange={(e) => setEquipmentForm({...equipmentForm, name: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <CategoryManager
                categoryType="equipment"
                label="Category"
                onCategoriesChange={fetchCategories}
                showAsDropdown={true}
                selectedValue={equipmentForm.category}
                onSelectionChange={(v) => setEquipmentForm({...equipmentForm, category: v})}
                allowAll={false}
              />
            </div>
            <div className="space-y-2">
              <Label>Serial Number</Label>
              <Input value={equipmentForm.serial_number} onChange={(e) => setEquipmentForm({...equipmentForm, serial_number: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Condition</Label>
              <Select value={equipmentForm.condition} onValueChange={(v) => setEquipmentForm({...equipmentForm, condition: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="excellent">Excellent</SelectItem>
                  <SelectItem value="good">Good</SelectItem>
                  <SelectItem value="fair">Fair</SelectItem>
                  <SelectItem value="poor">Poor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Purchase Date</Label>
              <Input type="date" value={equipmentForm.purchase_date} onChange={(e) => setEquipmentForm({...equipmentForm, purchase_date: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Purchase Price</Label>
              <Input type="number" step="0.01" value={equipmentForm.purchase_price} onChange={(e) => setEquipmentForm({...equipmentForm, purchase_price: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Warranty Expiry</Label>
              <Input type="date" value={equipmentForm.warranty_expiry} onChange={(e) => setEquipmentForm({...equipmentForm, warranty_expiry: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Location</Label>
              <Input value={equipmentForm.location} onChange={(e) => setEquipmentForm({...equipmentForm, location: e.target.value})} placeholder="e.g., Warehouse, Van #1" />
            </div>
            <div className="space-y-2">
              <Label>Assigned To</Label>
              <Select value={equipmentForm.assigned_to || "unassigned"} onValueChange={(v) => setEquipmentForm({...equipmentForm, assigned_to: v === "unassigned" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Last Inspection Date</Label>
              <Input type="date" value={equipmentForm.last_inspection_date} onChange={(e) => setEquipmentForm({...equipmentForm, last_inspection_date: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Next Inspection Date</Label>
              <Input type="date" value={equipmentForm.next_inspection_date} onChange={(e) => setEquipmentForm({...equipmentForm, next_inspection_date: e.target.value})} />
            </div>
            <div className="space-y-2 flex items-center gap-2">
              <input type="checkbox" id="cert-required" checked={equipmentForm.certification_required} onChange={(e) => setEquipmentForm({...equipmentForm, certification_required: e.target.checked})} />
              <Label htmlFor="cert-required">Certification Required</Label>
            </div>
            {equipmentForm.certification_required && (
              <div className="space-y-2">
                <Label>Certification Expiry</Label>
                <Input type="date" value={equipmentForm.certification_expiry} onChange={(e) => setEquipmentForm({...equipmentForm, certification_expiry: e.target.value})} />
              </div>
            )}
            <div className="col-span-2 space-y-2">
              <Label>Notes</Label>
              <Textarea value={equipmentForm.notes} onChange={(e) => setEquipmentForm({...equipmentForm, notes: e.target.value})} rows={2} />
            </div>
          </div>
          </div>
          <DialogFooter className="flex-shrink-0 border-t p-6 pt-4 bg-background">
            <Button variant="outline" onClick={() => setEquipmentDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateEquipment} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editMode ? "Update" : "Add"} Equipment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Maintenance Dialog */}
      <Dialog open={maintenanceDialogOpen} onOpenChange={setMaintenanceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Maintenance Record</DialogTitle>
            <DialogDescription>Record maintenance, inspection, or repair</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select value={maintenanceForm.maintenance_type} onValueChange={(v) => setMaintenanceForm({...maintenanceForm, maintenance_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inspection">Inspection</SelectItem>
                    <SelectItem value="repair">Repair</SelectItem>
                    <SelectItem value="cleaning">Cleaning</SelectItem>
                    <SelectItem value="certification">Certification</SelectItem>
                    <SelectItem value="replacement">Part Replacement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input type="date" value={maintenanceForm.date} onChange={(e) => setMaintenanceForm({...maintenanceForm, date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Cost</Label>
                <Input type="number" step="0.01" value={maintenanceForm.cost} onChange={(e) => setMaintenanceForm({...maintenanceForm, cost: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Performed By</Label>
                <Input value={maintenanceForm.performed_by} onChange={(e) => setMaintenanceForm({...maintenanceForm, performed_by: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Next Due Date</Label>
                <Input type="date" value={maintenanceForm.next_due_date} onChange={(e) => setMaintenanceForm({...maintenanceForm, next_due_date: e.target.value})} />
              </div>
              {maintenanceForm.maintenance_type === "inspection" && (
                <div className="space-y-2">
                  <Label>Passed Inspection</Label>
                  <Select 
                    value={maintenanceForm.passed_inspection === null ? "" : maintenanceForm.passed_inspection.toString()} 
                    onValueChange={(v) => setMaintenanceForm({...maintenanceForm, passed_inspection: v === "" ? null : v === "true"})}
                  >
                    <SelectTrigger><SelectValue placeholder="Select result" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Not specified</SelectItem>
                      <SelectItem value="true">Passed</SelectItem>
                      <SelectItem value="false">Failed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={maintenanceForm.description} onChange={(e) => setMaintenanceForm({...maintenanceForm, description: e.target.value})} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMaintenanceDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddMaintenance} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EquipmentManagement;
